import type { SituacaoFatura } from '@arena/shared/dominio';
import {
  mensalidadeDa,
  ROTULO_FORMA_PAGAMENTO,
  type ConsumoDaComanda,
  type ConsumoPendente,
  type FaturaNaLista,
  type FormaDoRecebimento,
} from '@arena/shared/schemas';
import type { Situacao } from '../ui/primitivos.js';

/**
 * Os textos de planos e cobranças. Funções puras — nenhuma toca a rede.
 *
 * As CONTAS não moram aqui: vencimento, multa, juros e reajuste são de
 * `@arena/shared/schemas`, as mesmas funções que o servidor usa. Aqui só o
 * que a tela escreve e a cor que cada situação recebe.
 */

/** A cor do selo — o vocabulário do protótipo: s-ok, s-wait, s-late. */
export const SITUACAO_DA_FATURA: Readonly<Record<SituacaoFatura, Situacao>> = {
  Paga: 'ok',
  EmAberto: 'aguardando',
  Vencida: 'atrasada',
  Cancelada: 'neutra',
};

/** "Paga", "Em aberto", "12 dias em atraso", "Cancelada" — a coluna Situação. */
export function rotuloDaFatura(fatura: Pick<FaturaNaLista, 'situacao' | 'diasDeAtraso'>): string {
  switch (fatura.situacao) {
    case 'Vencida':
      return fatura.diasDeAtraso === 1
        ? '1 dia em atraso'
        : `${fatura.diasDeAtraso} dias em atraso`;
    case 'EmAberto':
      return 'Em aberto';
    case 'Paga':
      return 'Paga';
    case 'Cancelada':
      return 'Cancelada';
  }
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** "2026-09" → "setembro". */
export function nomeDoMes(mes: string): string {
  return MESES[Number(mes.slice(5, 7)) - 1] ?? mes;
}

/** "2026-09" → "Setembro/2026" — a coluna Competência da ficha. */
export function competenciaPorExtenso(competencia: string): string {
  const nome = nomeDoMes(competencia);
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)}/${competencia.slice(0, 4)}`;
}

/** "2026-09" → "Setembro de 2026" — o subtítulo de `#v-planos`. */
export function mesPorExtenso(mes: string): string {
  const nome = nomeDoMes(mes);
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)} de ${mes.slice(0, 4)}`;
}

/**
 * O que se digita no campo de percentual → centésimos de ponto.
 *
 * "6%", "6", "4,62" e "-2,5%" valem; "6.5" também, como alguém digita no
 * celular. Mais de duas casas não: o reajuste é gravado em centésimos.
 */
export function percentualParaCentesimos(texto: string): number | null {
  const limpo = texto.replace(/[%\s]/g, '').replace('−', '-').replace(',', '.');
  if (!/^-?\d+(\.\d{1,2})?$/.test(limpo)) return null;
  return Math.round(Number(limpo) * 100);
}

/** Centésimos → o que o campo mostra ao editar: 600 → "6", 462 → "4,62". */
export function centesimosParaCampo(centesimos: number): string {
  const inteiro = centesimos % 100 === 0;
  return inteiro
    ? String(centesimos / 100)
    : (centesimos / 100).toFixed(2).replace(/0$/, '').replace('.', ',');
}

/**
 * Centavos → "R$ 160,00", sempre com os centavos. É o formato de
 * `#m-reajuste` e da ficha: "De R$ 160,00 para R$ 169,60" lado a lado
 * precisa das mesmas casas nos dois números.
 */
export function dinheiroComCentavos(centavos: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(centavos / 100)
    .replace(/\s/g, ' ');
}

export function rotuloDaForma(forma: FormaDoRecebimento | null): string {
  return forma ? ROTULO_FORMA_PAGAMENTO[forma] : '—';
}

/** "Brenda Loureiro", ou "Tainá Ferraz +2" no plano família. */
export function quemPaga(fatura: Pick<FaturaNaLista, 'titular' | 'outrosAlunos'>): string {
  return fatura.outrosAlunos > 0
    ? `${fatura.titular.nome} +${fatura.outrosAlunos}`
    : fatura.titular.nome;
}

// ---------------------------------------------------------------------
// A fatura com bar
// ---------------------------------------------------------------------

/** A fatura carrega consumo do bar — a tag "com bar" das tabelas. */
export function temBar(fatura: Pick<FaturaNaLista, 'consumo'>): boolean {
  return fatura.consumo > 0;
}

/**
 * A quebra que o recebimento mostra: mensalidade, bar, multa e juros, total.
 * `encargos` JÁ É o da mensalidade (`encargosDaFatura(mensalidadeDa(f), …)`);
 * o total soma o bar por cima, sem encargo sobre ele.
 */
export function quebraDaFatura(
  fatura: Pick<FaturaNaLista, 'valor' | 'consumo'>,
  encargos: number,
): { mensalidade: number; bar: number; encargos: number; total: number; comBar: boolean } {
  return {
    mensalidade: mensalidadeDa(fatura),
    bar: fatura.consumo,
    encargos,
    total: fatura.valor + encargos,
    comBar: temBar(fatura),
  };
}

/** Onde está o consumo de uma comanda lançada na mensalidade; `null` se não foi. */
export function rotuloDoConsumo(consumo: ConsumoDaComanda | undefined): string | null {
  if (!consumo) return null;
  switch (consumo.situacao) {
    case 'na_fatura': {
      const [ano, mes] = (consumo.competencia ?? '').split('-');
      return ano && mes ? `na fatura ${mes}/${ano}` : 'na fatura';
    }
    case 'pendente':
      return 'pendente — entra na próxima fatura';
    case 'cancelado':
      return 'cancelado';
  }
}

/** Há fatura que ainda vai vencer para receber o bar pendente (a derivada `EmAberto`)? */
export function haFaturaParaOBar(faturas: readonly Pick<FaturaNaLista, 'situacao'>[]): boolean {
  return faturas.some((f) => f.situacao === 'EmAberto');
}

/**
 * A dica do "Consumo do bar a faturar" quando NÃO há fatura aberta para
 * recebê-lo: o bar espera a próxima que nascer, e se o contrato não gera mais
 * nenhuma, ele não entra sozinho — a recepção precisa cobrar de outro jeito.
 */
export function textoDoConsumoPendente(
  pendente: ConsumoPendente,
  haFaturaAberta: boolean,
): string | null {
  if (pendente.quantidade === 0 || haFaturaAberta) return null;
  return 'Não há fatura em aberto para receber este valor. Ele entra na próxima fatura que for gerada; se o contrato não gera mais faturas, cobre de outro jeito.';
}
